import { create } from 'zustand';
import { logger } from 'src/utils/logger';

const FAVORITES_STORAGE_KEY = 'favoriteWorkflows';

interface FavoritesStore {
	favoriteWorkflowIds: string[];
	toggleFavorite: (workflowId: string) => void;
	isFavorite: (workflowId: string) => boolean;
}

function loadFavorites(): string[] {
	try {
		const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);

		if (!stored) return [];
		const parsed = JSON.parse(stored);

		if (!Array.isArray(parsed)) return [];

		return [...new Set(parsed.filter((id: unknown) => typeof id === 'string'))];
	} catch {
		return [];
	}
}

function persistFavorites(ids: string[]): void {
	try {
		localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(ids));
	} catch (error) {
		logger.warn('Failed to persist favorites to localStorage: ' + error);
	}
}

export const useFavoritesStore = create<FavoritesStore>((set, get) => ({
	favoriteWorkflowIds: loadFavorites(),
	toggleFavorite: (workflowId: string) => {
		const current = get().favoriteWorkflowIds;
		const updated = current.includes(workflowId)
			? current.filter((id) => id !== workflowId)
			: [...current, workflowId];

		persistFavorites(updated);
		set({ favoriteWorkflowIds: updated });
	},
	isFavorite: (workflowId: string) => {
		return get().favoriteWorkflowIds.includes(workflowId);
	},
}));

interface SortableOption {
	value?: string;
	label?: string;
}

export function sortWorkflows<T extends SortableOption>(
	workflows: T[],
	favoriteIds: string[]
): T[] {
	const favoriteSet = new Set(favoriteIds);

	return [...workflows].sort((a, b) => {
		const aFav = favoriteSet.has(a.value || '');
		const bFav = favoriteSet.has(b.value || '');

		if (aFav && !bFav) return -1;
		if (!aFav && bFav) return 1;

		const aLabel = (a.label || a.value || '').trim();
		const bLabel = (b.label || b.value || '').trim();

		return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
	});
}

export function sortWorkflowsWithTags<T extends SortableOption>(
	workflows: T[],
	favoriteIds: string[]
): (T & { labelTag?: string })[] {
	const favoriteSet = new Set(favoriteIds);

	return sortWorkflows(workflows, favoriteIds).map((opt) => ({
		...opt,
		labelTag: favoriteSet.has(opt.value || '') ? '★' : undefined,
	}));
}
